import {
    onValue,
    orderByChild,
    query,
    remove,
    set,
    ref,
    update,
} from "firebase/database";
import {
    FETCH_DOCUMENT_USERS,
    FETCH_DOCUMENT_USERS_SUCCESS,
    FETCH_DOCUMENT_USERS_FAILED,
    UPDATE_DOCUMENT_USERS,
} from "../store/types";
import { firebase } from "../config/configureFirebase";
import {
    getDownloadURL,
    uploadBytes,
    ref as storageRef,
} from "firebase/storage";

export const fetchDocumentUsers = () => (dispatch) => {
    try {
        const { documentUserRef } = firebase;

        if (!documentUserRef) {
            console.error(
                "Firebase documentUserRef is not properly initialized"
            );
            dispatch({
                type: FETCH_DOCUMENT_USERS_FAILED,
                payload: "Firebase reference not initialized",
            });
            return;
        }

        dispatch({
            type: FETCH_DOCUMENT_USERS,
            payload: null,
        });

        onValue(
            query(documentUserRef, orderByChild("created_at")),
            (snapshot) => {
                const data = snapshot.val();
                console.log("📥 Fetched document users from Firebase:", {
                    total_entries: data ? Object.keys(data).length : 0,
                    sample_entries: data ? Object.keys(data).slice(0, 3) : [],
                });

                if (data) {
                    const sortedData = Object.entries(data)
                        .sort(
                            ([, a], [, b]) =>
                                (b.created_at || 0) - (a.created_at || 0)
                        )
                        .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});

                    console.log("📥 Processed document users:", {
                        total_processed: Object.keys(sortedData).length,
                        sample_data: Object.values(sortedData)
                            .slice(0, 2)
                            .map((d) => ({
                                id: d.id,
                                user_id: d.user_id,
                                field_id: d.field_id,
                                status: d.status,
                            })),
                    });

                    dispatch({
                        type: FETCH_DOCUMENT_USERS_SUCCESS,
                        payload: sortedData,
                    });
                } else {
                    console.log("📥 No document users data found in Firebase");
                    dispatch({
                        type: FETCH_DOCUMENT_USERS_FAILED,
                        payload: "Unable to fetch document user settings.",
                    });
                }
            },
            (error) => {
                console.error("Firebase onValue error:", error);
                dispatch({
                    type: FETCH_DOCUMENT_USERS_FAILED,
                    payload: error.message,
                });
            }
        );
    } catch (error) {
        console.error("fetchDocumentUsers error:", error);
        dispatch({
            type: FETCH_DOCUMENT_USERS_FAILED,
            payload: error.message,
        });
    }
};

export const deleteDocumentUser = (id, onSuccess, onError) => (dispatch) => {
    try {
        const { documentUserEditRef } = firebase;

        if (!documentUserEditRef) {
            console.error(
                "Firebase documentUserEditRef is not properly initialized"
            );
            onError("Firebase documentUserEditRef not initialized");
            return;
        }

        remove(documentUserEditRef(id))
            .then(() => {
                console.log("Document user successfully deleted");
                onSuccess();
            })
            .catch((error) => {
                console.error("Error deleting document user:", error);
                onError(error);
            });
    } catch (error) {
        console.error("Error in deleteDocumentUser:", error);
        onError(error);
    }
};

export const updateDocumentUsers = (data, onSuccess, onError) => (dispatch) => {
    try {
        const { documentUserEditRef, singleUserRef } = firebase;

        if (!documentUserEditRef) {
            onError();
            return;
        }

        dispatch({
            type: UPDATE_DOCUMENT_USERS,
            payload: data,
        });

        console.log("new user data: ", data);
        console.log("📤 Saving document to Firebase:", {
            id: data.id,
            user_id: data.user_id,
            field_id: data.field_id,
            user_name: data.user_name,
            status: data.status,
            approved: data.approved,
            files_count: data.files?.length || 0,
            new_files_count: data.new_files?.length || 0,
            firebase_path: `document_users/${data.id}`,
        });

        console.log(
            "📤 Full document data being saved:",
            JSON.stringify(data, null, 2)
        );

        // Save document to document_users table
        set(documentUserEditRef(data.id), data);
        console.log(
            "✅ Document saved to Firebase successfully at path:",
            `document_users/${data.id}`
        );

        // Additionally save licenseImage and licenseImageBack to users table
        // Check if this is a driving license document by field name or ID
        const isLicenseDocument =
            data.field &&
            (data.field.name?.toLowerCase().includes("license") ||
                data.field.name?.toLowerCase().includes("driving") ||
                data.field.id === "legacy_license_front" ||
                data.field.id === "legacy_license_back" ||
                data.field_id === "legacy_license_front" ||
                data.field_id === "legacy_license_back");

        if (
            isLicenseDocument &&
            data.user_id &&
            (data.files || data.new_files)
        ) {
            try {
                console.log(
                    "📝 Detected license document, updating user profile:",
                    {
                        field_id: data.field_id,
                        field_name: data.field?.name,
                        user_id: data.user_id,
                        has_files: !!(data.files || data.new_files),
                    }
                );

                // Get the latest file URL from files or new_files
                let latestFile = null;
                if (
                    data.new_files &&
                    Array.isArray(data.new_files) &&
                    data.new_files.length > 0
                ) {
                    latestFile = data.new_files[data.new_files.length - 1];
                } else if (
                    data.files &&
                    Array.isArray(data.files) &&
                    data.files.length > 0
                ) {
                    latestFile = data.files[data.files.length - 1];
                }

                if (latestFile && latestFile.url) {
                    const updateData = {};

                    // Determine if this is front or back license
                    const isBackLicense =
                        data.field_id === "legacy_license_back" ||
                        data.field?.id === "legacy_license_back" ||
                        data.field?.name?.toLowerCase().includes("back");

                    if (isBackLicense) {
                        updateData.licenseImageBack = latestFile.url;
                        console.log(
                            "📝 Updating licenseImageBack in user profile:",
                            latestFile.url
                        );
                    } else {
                        updateData.licenseImage = latestFile.url;
                        console.log(
                            "📝 Updating licenseImage in user profile:",
                            latestFile.url
                        );
                    }

                    // Update user profile with license image
                    update(singleUserRef(data.user_id), updateData)
                        .then(() => {
                            console.log(
                                "✅ User profile updated with license image successfully"
                            );
                        })
                        .catch((error) => {
                            console.error(
                                "❌ Error updating user profile with license image:",
                                error
                            );
                        });
                }
            } catch (profileUpdateError) {
                console.error(
                    "❌ Error updating user profile with license:",
                    profileUpdateError
                );
                // Don't fail the main operation if profile update fails
            }
        }

        onSuccess();
    } catch (error) {
        onError();
    }
};

export const uploadImageToStorage = async (imageFile, path, options = {}) => {
    try {
        const { storage } = firebase;
        if (!storage) {
            throw new Error("Firebase storage is not initialized");
        }

        // Create a reference to the storage location
        const imageRef = storageRef(storage, path);

        // Upload the file
        const snapshot = await uploadBytes(imageRef, imageFile);

        // Get the download URL
        const downloadURL = await getDownloadURL(snapshot.ref);

        return downloadURL;
    } catch (error) {
        console.error("Error uploading image:", error);
        if (options.onError) {
            options.onError(error);
        }
        throw error;
    }
};
